import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Plus, Pencil } from 'lucide-react'
import { createAuditorApi, updateAuditorApi } from '@frontend/lib/api'
import type { AuditorDetail } from '@common/types'

/** The fields the edit modal actually needs — avoids requiring a full AuditorDetail */
type EditableAuditor = Pick<AuditorDetail, 'id' | 'name' | 'website' | 'opinion' | 'status'>
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface AddAuditorModalProps {
  mode: 'create'
  open: boolean
  onOpenChange: (open: boolean) => void
  auditor?: never
}

interface EditAuditorModalProps {
  mode: 'edit'
  open: boolean
  onOpenChange: (open: boolean) => void
  auditor: EditableAuditor
}

type AuditorModalProps = AddAuditorModalProps | EditAuditorModalProps

interface FormData {
  name: string
  website: string
  opinion: string
}

const emptyFormData: FormData = {
  name: '',
  website: '',
  opinion: '',
}

export function AuditorModal(props: AuditorModalProps) {
  const { mode, open, onOpenChange } = props
  const auditor = mode === 'edit' ? props.auditor : undefined
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<FormData>({ ...emptyFormData })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setFormData(
        auditor
          ? { name: auditor.name, website: auditor.website ?? '', opinion: auditor.opinion ?? '' }
          : { ...emptyFormData }
      )
      setError(null)
    }
  }, [open, auditor])

  const createMutation = useMutation({
    mutationFn: createAuditorApi,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auditors'] })
      onOpenChange(false)
      toast.success(`Auditor "${data.auditor.name}" created`)
    },
    onError: (err) => handleMutationError(err, 'Failed to create auditor'),
  })

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateAuditorApi>[1]) => updateAuditorApi(auditor!.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auditors'] })
      queryClient.invalidateQueries({ queryKey: ['auditor', auditor!.id] })
      queryClient.invalidateQueries({ queryKey: ['auditor-history', auditor!.id] })
      onOpenChange(false)
      toast.success(`Auditor "${data.auditor.name}" updated`)
    },
    onError: (err) => handleMutationError(err, 'Failed to update auditor'),
  })

  function handleMutationError(err: Error, fallback: string) {
    if (isHTTPError(err)) {
      const data = err.data as { error?: string }
      setError(data.error || fallback)
    } else {
      setError(fallback)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    // Cannot clear opinion on a published auditor
    if (mode === 'edit' && auditor?.status === 'published' && auditor.opinion && !formData.opinion.trim()) {
      setError('Cannot clear the opinion on a published auditor')
      return
    }

    if (mode === 'create') {
      createMutation.mutate({
        name: formData.name.trim(),
        website: formData.website.trim() || null,
      })
    } else {
      updateMutation.mutate({
        name: formData.name.trim(),
        website: formData.website.trim() || null,
        opinion: formData.opinion.trim() || null,
      })
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isCreate = mode === 'create'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCreate ? <Plus className="h-5 w-5" /> : <Pencil className="h-5 w-5" />}
            {isCreate ? 'Add Auditor' : 'Edit Auditor'}
          </DialogTitle>
          <DialogDescription>
            {isCreate ? 'Add a new auditing firm to track.' : 'Update auditor details and opinion.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="auditor-name" className="block text-sm font-medium mb-1">
              Auditor Name <span className="text-red-500">*</span>
            </label>
            <input
              id="auditor-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Deloitte"
              disabled={isSubmitting}
              autoComplete="off"
              data-1p-ignore
            />
          </div>

          <div>
            <label htmlFor="auditor-website" className="block text-sm font-medium mb-1">
              Website
            </label>
            <input
              id="auditor-website"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://..."
              disabled={isSubmitting}
            />
          </div>

          {mode === 'edit' && (
            <div>
              <label htmlFor="auditor-opinion" className="block text-sm font-medium mb-1">
                Riveron&apos;s Opinion
              </label>
              <textarea
                id="auditor-opinion"
                value={formData.opinion}
                onChange={(e) => setFormData((prev) => ({ ...prev, opinion: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px]"
                placeholder="Enter Riveron's qualified opinion of this auditor..."
                disabled={isSubmitting}
              />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {isCreate ? 'Creating...' : 'Saving...'}
                </>
              ) : isCreate ? (
                'Add Auditor'
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
